import { ref, onMounted, computed, getCurrentInstance, watch } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment'
import apiUseCase from '@/usecase/apiUseCase'
import returnUrlPrams from '@/utils/helpers/returnUrlPrams'
import { ITrayek, IFuelTransactionListItem } from '@/utils/interfaces/iFuelMonitoring'
import { useApprovalStatus } from '@/composables/useApprovalStatus'

export function useFuelMonitoring() {
  const store = useStore()
  const app = getCurrentInstance()
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { $toast } = app!.appContext.config.globalProperties

  // State
  const filterDate = ref<Date[]>([])
  const searchQuery = ref('')

  // Trayek State (Level 1)
  const trayekList = ref<ITrayek[]>([])
  const isLoadingTrayeks = ref(false)
  const expandedTrayekIds = ref<string[]>([])
  const trayekPaginate = ref({
    pageSize: 10,
    pageNumber: 1,
    totalRecords: 0
  })

  // PP Transactions per Trayek (Level 2) - keyed by trayekId
  const trayekTransactions = ref<Record<string, IFuelTransactionListItem[]>>({})
  const trayekTransactionLoading = ref<Record<string, boolean>>({})
  const trayekTransactionPaginate = ref<Record<string, { pageSize: number; pageNumber: number; totalRecords: number }>>({})

  // Expanded PP rows per Trayek (Level 3) - keyed by trayekId
  const expandedPpRows = ref<Record<string, any[]>>({})

  // Non-PP Transaction State
  const transactionNonPpList = ref<any[]>([])
  const isLoadingTransactionNonPp = ref(false)
  const expandedTransactionNonPpRows = ref<any[]>([])
  const transactionNonPpPaginate = ref({
    pageSize: 5,
    pageNumber: 1,
    totalRecords: 0
  })

  // Filter Drawer State
  const isFilterDrawerOpen = ref(false)

  // Selected Filter States (Draft states inside drawer)
  const selectedFuelUsageStatuses = ref<string[]>([])
  const selectedApprovalStatuses = ref<string[]>([])
  const selectedPartnerIds = ref<string[]>([])

  // Applied Filter States (Committed states shown in pills & sent to API)
  const appliedFuelUsageStatuses = ref<string[]>([])
  const appliedApprovalStatuses = ref<string[]>([])
  const appliedPartnerIds = ref<string[]>([])

  // Options Data
  const fuelUsageStatusOptions = ref([
    { Name: 'Under', Description: 'Under' },
    { Name: 'Normal', Description: 'Normal' },
    { Name: 'Warning', Description: 'Warning' },
    { Name: 'Over', Description: 'Over' }
  ])
  const { approvalStatuses: approvalStatusOptions, fetchApprovalStatuses } = useApprovalStatus()
  const partnerOptions = ref<any[]>([])

  // Utility: Fuel usage status label
  const getStatusLabel = (status?: string) => {
    if (!status) return '-'
    const map: Record<string, string> = {
      Under: 'KURANG',
      Normal: 'NORMAL',
      Warning: 'WARNING',
      Over: 'OVER'
    }
    return map[status] || status.toUpperCase()
  }

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      fetchApprovalStatuses()
      const partnerRes = await apiUseCase.get('/management/v2/FuelStationPartners', '?pageSize=100&pageNumber=1')

      if (!partnerRes.error) partnerOptions.value = partnerRes.result || []
    } catch (err) {
      console.error('Error fetching filter options', err)
    }
  }

  // Build common filter params for transactions
  const buildTransactionFilterParams = () => {
    const customFilters: string[][] = []

    // Date range
    if (filterDate.value && filterDate.value.length > 0) {
      const start = filterDate.value[0]
      const end = filterDate.value[1] || filterDate.value[0]
      if (start) {
        customFilters.push(['startDate', '=', moment(start as any).format('YYYY-MM-DD')])
        customFilters.push(['endDate', '=', moment(end as any).format('YYYY-MM-DD')])
      }
    }

    // Checkbox filters
    if (appliedFuelUsageStatuses.value.length > 0) {
      appliedFuelUsageStatuses.value.forEach((val) => {
        customFilters.push(['fuelUsageStatuses', '=', val])
      })
    }
    if (appliedApprovalStatuses.value.length > 0) {
      appliedApprovalStatuses.value.forEach((val) => {
        customFilters.push(['fuelingTransactionApprovalStatuses', '=', val])
      })
    }
    if (appliedPartnerIds.value.length > 0) {
      appliedPartnerIds.value.forEach((val) => {
        customFilters.push(['fuelStationPartnerIds', '=', val])
      })
    }

    return customFilters
  }

  // ========================
  // Level 1: Fetch Trayeks
  // ========================
  const fetchTrayeks = async () => {
    isLoadingTrayeks.value = true
    store.dispatch('showLoading')

    const queryObj: any = {
      pageSize: trayekPaginate.value.pageSize,
      pageNumber: trayekPaginate.value.pageNumber,
      custom: [['orderBy', '=', 'trayekDetailsCount desc']]
    }

    const queryStr = returnUrlPrams(queryObj)
    const endpoint = '/management/v2/Trayeks'

    try {
      const response = await apiUseCase.get(endpoint, queryStr)
      if (!response.error) {
        trayekList.value = response.result || []
        trayekPaginate.value.totalRecords = response.count || 0
      } else {
        $toast.add({ severity: 'error', detail: response.message, group: 'bc', life: 3000 })
      }
    } catch (err) {
      console.error(err)
    } finally {
      isLoadingTrayeks.value = false
      store.dispatch('hideLoading')
    }
  }

  // ==========================================
  // Level 2: Fetch PP Transactions per Trayek
  // ==========================================
  const fetchTrayekTransactions = async (trayekId: string) => {
    trayekTransactionLoading.value = { ...trayekTransactionLoading.value, [trayekId]: true }

    // Initialize paginate if not exists
    if (!trayekTransactionPaginate.value[trayekId]) {
      trayekTransactionPaginate.value = {
        ...trayekTransactionPaginate.value,
        [trayekId]: { pageSize: 5, pageNumber: 1, totalRecords: 0 }
      }
    }

    const pag = trayekTransactionPaginate.value[trayekId]
    const customFilters = buildTransactionFilterParams()
    customFilters.push(['trayekId', '=', trayekId])

    const queryObj: any = {
      search: searchQuery.value,
      filterField: ['q'],
      custom: customFilters,
      pageSize: pag.pageSize,
      pageNumber: pag.pageNumber
    }

    const queryStr = returnUrlPrams(queryObj)
    const endpoint = '/transaction/v2/TransactionPps/FuelingTransactions'

    try {
      const response = await apiUseCase.get(endpoint, queryStr)
      if (!response.error) {
        trayekTransactions.value = { ...trayekTransactions.value, [trayekId]: response.result || [] }
        trayekTransactionPaginate.value = {
          ...trayekTransactionPaginate.value,
          [trayekId]: { ...pag, totalRecords: response.count || 0 }
        }
      } else {
        $toast.add({ severity: 'error', detail: response.message, group: 'bc', life: 3000 })
      }
    } catch (err) {
      console.error(err)
    } finally {
      trayekTransactionLoading.value = { ...trayekTransactionLoading.value, [trayekId]: false }
    }
  }

  // ===============================
  // Level 1 Handlers: Trayek
  // ===============================
  const onExpandTrayek = (trayekId: string) => {
    const isExpanded = expandedTrayekIds.value.includes(trayekId)

    if (isExpanded) {
      // Collapse
      expandedTrayekIds.value = expandedTrayekIds.value.filter((id) => id !== trayekId)
    } else {
      // Expand — collapse others + collapse Non-PP
      expandedTrayekIds.value = [trayekId]
      expandedTransactionNonPpRows.value = []

      // Lazy-load: fetch transactions only if not already loaded
      if (!trayekTransactions.value[trayekId]) {
        fetchTrayekTransactions(trayekId)
      }
    }
  }

  const onChangeTrayekPage = (event: any) => {
    trayekPaginate.value.pageNumber = event.page + 1
    // Reset expanded states when changing page
    expandedTrayekIds.value = []
    expandedPpRows.value = {}
    fetchTrayeks()
  }

  // ===============================
  // Level 2 Handlers: PP per Trayek
  // ===============================
  const onChangePpPage = (trayekId: string, event: any) => {
    trayekTransactionPaginate.value = {
      ...trayekTransactionPaginate.value,
      [trayekId]: {
        ...trayekTransactionPaginate.value[trayekId],
        pageNumber: event.page + 1
      }
    }
    // Collapse Level 3 expanded rows for this trayek
    expandedPpRows.value = { ...expandedPpRows.value, [trayekId]: [] }
    fetchTrayekTransactions(trayekId)
  }

  // ===============================
  // Level 3 Handlers: PP Row Expand
  // ===============================
  const onExpandPpRow = (trayekId: string, newRows: any[]) => {
    expandedPpRows.value = { ...expandedPpRows.value, [trayekId]: newRows }
  }

  // ==========================================
  // Non-PP Transaction (unchanged logic)
  // ==========================================
  const fetchTransactionNonPpData = async () => {
    isLoadingTransactionNonPp.value = true

    const customFilters: string[][] = [
      ['transactionTypes', '=', 'Voucher']
    ]

    // Check if range date is selected properly
    if (filterDate.value && filterDate.value.length > 0) {
      const start = filterDate.value[0]
      const end = filterDate.value[1] || filterDate.value[0]
      if (start) {
        customFilters.push(['startDate', '=', moment(start as any).format('YYYY-MM-DD')])
        customFilters.push(['endDate', '=', moment(end as any).format('YYYY-MM-DD')])
      }
    }

    // Add checkbox filters using custom
    if (appliedFuelUsageStatuses.value.length > 0) {
      appliedFuelUsageStatuses.value.forEach((val) => {
        customFilters.push(['fuelUsageStatuses', '=', val])
      })
    }
    if (appliedApprovalStatuses.value.length > 0) {
      appliedApprovalStatuses.value.forEach((val) => {
        customFilters.push(['approvalStatuses', '=', val])
      })
    }
    if (appliedPartnerIds.value.length > 0) {
      appliedPartnerIds.value.forEach((val) => {
        customFilters.push(['fuelStationPartnerId', '=', val])
      })
    }

    const queryObj: any = {
      search: searchQuery.value,
      filterField: ['q'],
      custom: customFilters,
      pageSize: transactionNonPpPaginate.value.pageSize,
      pageNumber: transactionNonPpPaginate.value.pageNumber
    }

    const queryStr = returnUrlPrams(queryObj)
    const endpoint = '/transaction/v2/FuelingTransactions'

    try {
      const response = await apiUseCase.get(endpoint, queryStr)
      if (!response.error) {
        transactionNonPpList.value = response.result || []
        transactionNonPpPaginate.value.totalRecords = response.count || 0
      }
    } catch (err) {
      console.error(err)
    } finally {
      isLoadingTransactionNonPp.value = false
    }
  }

  const onChangeTransactionNonPpPage = (event: any) => {
    transactionNonPpPaginate.value.pageNumber = event.page + 1
    fetchTransactionNonPpData()
  }

  // ==========================================
  // Non-PP expand handler
  // ==========================================
  const onExpandedTransactionNonPpRowsChange = (newRows: any[]) => {
    if (newRows.length > 0) {
      // Non-PP expanded → close all Trayek expansions
      expandedTrayekIds.value = []
      expandedPpRows.value = {}
    }
    expandedTransactionNonPpRows.value = newRows
  }

  const transactionNonPpRowData = computed(() => [{ Id: 'transaction-non-pp-row' }])

  // ==========================================
  // Filter Logic
  // ==========================================
  const applyFilter = () => {
    // Commit draft states to active applied states
    appliedFuelUsageStatuses.value = [...selectedFuelUsageStatuses.value]
    appliedApprovalStatuses.value = [...selectedApprovalStatuses.value]
    appliedPartnerIds.value = [...selectedPartnerIds.value]

    // Reset pagination & expanded states
    trayekPaginate.value.pageNumber = 1
    transactionNonPpPaginate.value.pageNumber = 1
    expandedTrayekIds.value = []
    expandedPpRows.value = {}
    trayekTransactions.value = {}
    trayekTransactionPaginate.value = {}

    isFilterDrawerOpen.value = false
    fetchTrayeks()
    fetchTransactionNonPpData()
  }

  const activeFilters = computed(() => {
    const list: Array<{ id: string; category: string; value: any; label: string }> = []

    // Fuel Usage Statuses
    appliedFuelUsageStatuses.value.forEach((val) => {
      list.push({
        id: `usage_${val}`,
        category: 'fuelUsage',
        value: val,
        label: getStatusLabel(val)
      })
    })

    // Approval Statuses
    appliedApprovalStatuses.value.forEach((val) => {
      const opt = approvalStatusOptions.value.find((o) => o.Name === val)
      list.push({
        id: `appr_${val}`,
        category: 'approvalStatus',
        value: val,
        label: opt ? opt.Description : val
      })
    })

    // Partner IDs
    appliedPartnerIds.value.forEach((val) => {
      const opt = partnerOptions.value.find((o) => o.Id === val)
      list.push({
        id: `partner_${val}`,
        category: 'partner',
        value: val,
        label: opt ? opt.Name : val
      })
    })

    return list
  })

  const removeFilter = (filterItem: { category: string; value: any }) => {
    if (filterItem.category === 'fuelUsage') {
      appliedFuelUsageStatuses.value = appliedFuelUsageStatuses.value.filter((v) => v !== filterItem.value)
      selectedFuelUsageStatuses.value = [...appliedFuelUsageStatuses.value]
    } else if (filterItem.category === 'approvalStatus') {
      appliedApprovalStatuses.value = appliedApprovalStatuses.value.filter((v) => v !== filterItem.value)
      selectedApprovalStatuses.value = [...appliedApprovalStatuses.value]
    } else if (filterItem.category === 'partner') {
      appliedPartnerIds.value = appliedPartnerIds.value.filter((v) => v !== filterItem.value)
      selectedPartnerIds.value = [...appliedPartnerIds.value]
    }

    // Reset & refresh
    trayekPaginate.value.pageNumber = 1
    transactionNonPpPaginate.value.pageNumber = 1
    expandedTrayekIds.value = []
    expandedPpRows.value = {}
    trayekTransactions.value = {}
    trayekTransactionPaginate.value = {}

    fetchTrayeks()
    fetchTransactionNonPpData()
  }

  // ==========================================
  // Watchers
  // ==========================================
  watch(
    () => filterDate.value,
    (newVal) => {
      // Check if newVal is empty, null, or [null, null]
      if (!newVal || newVal.length === 0 || (newVal.length > 0 && !newVal[0] && !newVal[1])) {
        trayekPaginate.value.pageNumber = 1
        transactionNonPpPaginate.value.pageNumber = 1
        expandedTrayekIds.value = []
        expandedPpRows.value = {}
        trayekTransactions.value = {}
        trayekTransactionPaginate.value = {}
        fetchTrayeks()
        fetchTransactionNonPpData()
        return
      }

      const start = newVal[0]
      const end = newVal[1]

      if (start && end) {
        trayekPaginate.value.pageNumber = 1
        transactionNonPpPaginate.value.pageNumber = 1
        expandedTrayekIds.value = []
        expandedPpRows.value = {}
        trayekTransactions.value = {}
        trayekTransactionPaginate.value = {}
        fetchTrayeks()
        fetchTransactionNonPpData()
      }
    },
    { deep: true }
  )

  watch(
    () => isFilterDrawerOpen.value,
    (isOpen) => {
      if (isOpen) {
        // Sync draft states from active states when opening the drawer
        selectedFuelUsageStatuses.value = [...appliedFuelUsageStatuses.value]
        selectedApprovalStatuses.value = [...appliedApprovalStatuses.value]
        selectedPartnerIds.value = [...appliedPartnerIds.value]
      }
    }
  )

  // ==========================================
  // Lifecycle
  // ==========================================
  onMounted(() => {
    store.dispatch('ui/setBreadcrumbs', [])
    fetchFilterOptions()
    fetchTrayeks()
    fetchTransactionNonPpData()
  })

  return {
    filterDate,
    searchQuery,

    // Trayek (Level 1)
    trayekList,
    isLoadingTrayeks,
    expandedTrayekIds,
    trayekPaginate,

    // PP Transactions per Trayek (Level 2)
    trayekTransactions,
    trayekTransactionLoading,
    trayekTransactionPaginate,

    // Expanded PP rows (Level 3)
    expandedPpRows,

    // Trayek handlers
    onExpandTrayek,
    onChangeTrayekPage,
    onChangePpPage,
    onExpandPpRow,
    fetchTrayeks,
    fetchTrayekTransactions,

    // Non-PP
    transactionNonPpList,
    isLoadingTransactionNonPp,
    expandedTransactionNonPpRows,
    transactionNonPpPaginate,
    transactionNonPpRowData,
    fetchTransactionNonPpData,
    onChangeTransactionNonPpPage,
    onExpandedTransactionNonPpRowsChange,

    // Filter
    isFilterDrawerOpen,
    selectedFuelUsageStatuses,
    selectedApprovalStatuses,
    selectedPartnerIds,
    fuelUsageStatusOptions,
    approvalStatusOptions,
    partnerOptions,
    applyFilter,
    activeFilters,
    removeFilter,

    // Utils
    getStatusLabel
  }
}
